import React, { useState, useEffect } from 'react';
import { cantieri as cantieriApi } from '../../utils/api';
import { documenti as documentiApi } from '../../utils/api';
import { CANTIERE_STATI } from '../../constants/cantieriStati';
import useOptimisticMutation from '../../hooks/useOptimisticMutation';
import LimiteCantieriModal from './LimiteCantieriModal';
import { limiteCantieriDaErrore } from './limiteCantieri';
import { statoPagamentoDaFatture } from './statoPagamenti';

// Value/label/colore degli stati arrivano dalla fonte unica (CANTIERE_STATI),
// allineata al backend e alla lista (DBT-050). Qui restano solo le icone,
// presentazione locale del dettaglio.
const STATO_ICON = {
  nuovo:       '🏗️',
  sopralluogo: '🔍',
  in_corso:    '●',
  sospeso:     '⚠️',
  completato:  '✅',
  annullato:   '🔴',
};

const PRIORITA = [
  { value: 'bassa',   label: 'Bassa',    icon: '🟦', color: '#CBD5E0' },
  { value: 'media',   label: 'Normale',  icon: '🟩', color: 'var(--brand)' },
  { value: 'alta',    label: 'Alta',     icon: '🟨', color: '#F5A623' },
  { value: 'urgente', label: 'Urgente',  icon: '🔴', color: '#E85D5D' },
];

export default function StatoTab({ data, cantiereId, onRefresh, patchCantiereLocal, onStatoChanged, showToast }) {
  const currentStato = data?.cantiere?.status || '';
  const [saving, setSaving] = useState(false);
  // CAP-018 — riaprire un cantiere chiuso oltre il limite: modale dedicato.
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [cantiereData, setCantiereData] = useState(null);
  const { run: runStato, pending: statoPending } = useOptimisticMutation();

  useEffect(() => {
    cantieriApi.get(cantiereId).then(r => {
      setCantiereData(r.data);
      setTags(r.data?.tags || []);
    }).catch(() => {});
    documentiApi.getTag?.()?.then?.(r => setAllTags(r.data?.results || r.data || []))?.catch?.(() => {});
  }, [cantiereId]);

  const currentPriorita = cantiereData?.priorita || data?.cantiere?.priorita || 'media';

  // Stato pagamenti derivato dalle fatture — DBT-053 (13/10): le annullate non
  // contano (statoPagamenti.js).
  const statoPagamento = statoPagamentoDaFatture(data?.fatture);

  const PAGAMENTI = [
    { value: 'parziale',   label: 'Parzialmente pagato', color: '#F5A623' },
    { value: 'saldato',    label: 'Saldato',            color: 'var(--brand)' },
    { value: 'in_ritardo', label: 'In ritardo',         color: '#E85D5D' },
  ];

  // UX-RT-051 — cambio stato ottimistico: aggiorna subito modale (chiave `status`)
  // e badge di riga in lista (chiave `stato`), rollback + toast su errore.
  const handleStato = async (stato) => {
    if (stato === currentStato || statoPending) return;
    if (!window.confirm('Confermi il cambio stato?')) return;
    const prev = currentStato;
    try {
      await runStato({
        optimistic: () => {
          patchCantiereLocal?.({ status: stato });
          onStatoChanged?.(cantiereId, stato);
        },
        commit: () => cantieriApi.cambiaStato(cantiereId, stato),
        rollback: () => {
          patchCantiereLocal?.({ status: prev });
          onStatoChanged?.(cantiereId, prev);
        },
      });
      showToast?.('Stato aggiornato.', 'success');
    } catch (e) {
      const limite = limiteCantieriDaErrore(e);
      if (limite) setLimiteInfo(limite);
      else showToast?.('Impossibile aggiornare lo stato. Riprova.', 'error');
    }
  };

  const handlePriorita = async (priorita) => {
    if (saving) return;
    setSaving(true);
    try { await cantieriApi.update(cantiereId, { priorita }); if (onRefresh) onRefresh(); setCantiereData(p => ({ ...p, priorita })); } catch {}
    finally { setSaving(false); }
  };

  const handleAddTag = async (tagId) => {
    const newTags = [...tags, tagId];
    try { await cantieriApi.update(cantiereId, { tags: newTags }); setTags(newTags); } catch {}
  };

  const handleRemoveTag = async (tagId) => {
    const newTags = tags.filter(t => t !== tagId);
    try { await cantieriApi.update(cantiereId, { tags: newTags }); setTags(newTags); } catch {}
  };

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Stato cantiere</div>

      {/* Stato lavori */}
      <Section title="STATO LAVORI">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CANTIERE_STATI.map(s => (
            <Chip key={s.value} label={s.label} icon={STATO_ICON[s.value]} color={s.color}
              active={currentStato === s.value} onClick={() => handleStato(s.value)} disabled={statoPending} />
          ))}
        </div>
      </Section>

      {/* Stato pagamenti (read-only, derivato) */}
      {statoPagamento !== 'nessuna' && (
        <Section title="STATO PAGAMENTI">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PAGAMENTI.map(p => (
              <Chip key={p.value} label={p.label} color={p.color}
                active={statoPagamento === p.value} onClick={() => {}} disabled />
            ))}
          </div>
        </Section>
      )}

      {/* Priorità */}
      <Section title="PRIORITÀ">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PRIORITA.map(p => (
            <Chip key={p.value} label={p.label} icon={p.icon} color={p.color}
              active={currentPriorita === p.value} onClick={() => handlePriorita(p.value)} disabled={saving} />
          ))}
        </div>
      </Section>

      {/* Tag cantiere */}
      <Section title="TAG CANTIERE">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {allTags.filter(t => tags.includes(t.id)).map(t => (
            <span key={t.id} onClick={() => handleRemoveTag(t.id)} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
              background: (t.colore || '#2EA084') + '20',
              color: t.colore || 'var(--brand)',
              border: '1px solid ' + (t.colore || '#2EA084') + '40',
            }}>
              {t.nome} ×
            </span>
          ))}
          {allTags.filter(t => !tags.includes(t.id)).length > 0 && (
            <select onChange={e => { if (e.target.value) handleAddTag(e.target.value); e.target.value = ''; }}
              style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, border: '1px dashed var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer' }}>
              <option value="">+ Aggiungi tag</option>
              {allTags.filter(t => !tags.includes(t.id)).map(t => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
          )}
        </div>
      </Section>

      <LimiteCantieriModal info={limiteInfo} onClose={() => setLimiteInfo(null)} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      padding: '16px 20px', borderRadius: 'var(--radius-card)', marginBottom: 12,
      border: '1px solid var(--ds-border)', background: 'var(--chip)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({ label, icon, color, active, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '8px 16px', borderRadius: 20, fontSize: 13, cursor: disabled ? 'default' : 'pointer',
      border: active ? '2px solid ' + color : '1px solid var(--ds-border)',
      background: active ? `color-mix(in srgb, ${color} 15%, transparent)` : 'var(--ds-bg)',
      color: active ? color : 'var(--ds-muted)',
      fontWeight: active ? 500 : 400,
      display: 'flex', alignItems: 'center', gap: 6,
      transition: 'all 0.15s ease',
    }}>
      {icon && <span>{icon}</span>} {label}
    </button>
  );
}
